import React from "react";
import { Box, Spinner } from "@amzn/awsui-components-react";
import CourseCard from "./components/CourseCard";

/** Format a minutes total as "Xh Ym" (matching the prototype's rollup line). */
const formatMinutes = (mins) => {
  const m = Number(mins) || 0;
  if (m <= 0) return "0m";
  const h = Math.floor(m / 60);
  const rem = m % 60;
  if (h && rem) return `${h}h ${rem}m`;
  if (h) return `${h}h`;
  return `${rem}m`;
};

const tierClass = (tier) => (tier.code ? `raise-tier-${tier.code.toLowerCase()}` : "");

/**
 * The Core Curriculum matrix, rendered as a single aligned CSS grid (matching
 * the prototype): a fixed left "skill set" column plus one column per tier. The
 * header row (rollups) and every skill-set row share the same grid template, so
 * columns line up top to bottom. Each tier column is a continuous colored band.
 */
const CoreCurriculum = ({ curriculum, intro }) => {
  if (!curriculum) {
    return (
      <Box padding="l">
        <Spinner /> Loading curriculum...
      </Box>
    );
  }

  const tiers = [...(curriculum.tiers || [])].sort(
    (a, b) => (a.sort_order || 0) - (b.sort_order || 0)
  );
  const skillSets = [...(curriculum.skill_sets || [])].sort(
    (a, b) => (a.sort_order || 0) - (b.sort_order || 0)
  );
  const cells = curriculum.cells || [];
  const rollups = curriculum.rollups || {};

  // Index cells by "skillSetId#tierId" for O(1) lookup while rendering the grid.
  const cellIndex = {};
  cells.forEach((cell) => {
    cellIndex[`${cell.skill_set_id}#${cell.tier_id}`] = cell;
  });

  // One shared grid template: fixed left label column + equal tier columns.
  const gridStyle = {
    gridTemplateColumns: `220px repeat(${tiers.length}, 1fr)`,
  };

  return (
    <div className="raise-matrix-wrap">
      {intro ? (
        <Box variant="p" padding={{ bottom: "s" }}>
          {intro}
        </Box>
      ) : null}

      {/* Legend */}
      <div className="raise-legend">
        {tiers.map((tier) => (
          <span key={tier.tier_id} className={`raise-legend-item ${tierClass(tier)}`}>
            <span className="raise-legend-dot" />
            {tier.display_name}
          </span>
        ))}
      </div>

      <div className="raise-matrix" style={gridStyle}>
        {/* Header row: empty top-left corner + one tier header per column */}
        <div className="raise-grid-corner" />
        {tiers.map((tier) => {
          const r = rollups[tier.tier_id] || {};
          const count = Number(r.course_count) || 0;
          return (
            <div key={tier.tier_id} className={`raise-grid-head ${tierClass(tier)}`}>
              {count > 0 ? (
                <div className="raise-head-total">
                  {`${formatMinutes(r.total_minutes)} total`.toUpperCase()}
                </div>
              ) : null}
              <div className="raise-head-title">{tier.display_name}</div>
              {tier.description ? (
                <div className="raise-head-desc">{tier.description}</div>
              ) : null}
            </div>
          );
        })}

        {/* One row per skill set — label cell + one tier cell each */}
        {skillSets.map((skillSet) => (
          <React.Fragment key={skillSet.skill_set_id}>
            <div className="raise-grid-label">{skillSet.name}</div>
            {tiers.map((tier) => {
              const cell = cellIndex[`${skillSet.skill_set_id}#${tier.tier_id}`];
              const courses = (cell && cell.courses) || [];
              const notApplicable = !cell;
              return (
                <div
                  key={tier.tier_id}
                  className={`raise-grid-cell ${tierClass(tier)}`}
                >
                  {cell && cell.tagline ? (
                    <div className="raise-cell-tagline">{cell.tagline}</div>
                  ) : notApplicable ? (
                    <div className="raise-cell-empty">—</div>
                  ) : null}
                  {courses.map((course) => (
                    <CourseCard
                      key={course.course_id}
                      course={course}
                      tierCode={tier.code}
                    />
                  ))}
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default CoreCurriculum;
